import { Box, Group, SimpleGrid, Skeleton, Text, UnstyledButton } from '@mantine/core';
import {
  IconArrowRight,
  IconCalendarStats,
  IconCertificate,
  IconClockHour4,
  IconCube,
  IconFileAlert,
  IconFileCertificate,
  IconFileCheck,
  IconFileTypePdf,
  IconListCheck,
  IconMessage2,
  IconMessageCheck,
  IconMessageExclamation,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useClientSummary } from './api';

interface Tile {
  label: string;
  /** A count; a tracker tile has none and opens its list instead. */
  value?: number | undefined;
  tracker?: boolean;
  caption: ReactNode;
  to: string;
  icon: typeof IconFileCheck;
}

function DashboardTile({ c, loading }: { c: Tile; loading: boolean }) {
  return (
    <UnstyledButton
      component={Link}
      to={c.to}
      className="portal-count"
      aria-label={c.tracker ? `Open the ${c.label}` : `${c.label}: ${c.value ?? 'loading'}`}
      // The count and its caption sit at the bottom, so they line up across a row whatever the label's length.
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <Group gap={8} wrap="nowrap" align="flex-start">
        <c.icon size={18} stroke={1.7} color="var(--mantine-color-brand-7)" style={{ flex: 'none', marginTop: 1 }} aria-hidden />
        <Text fz="sm" fw={500} lh={1.3}>
          {c.label}
        </Text>
      </Group>
      <Box style={{ flex: 1 }} />
      {c.tracker ? (
        <Group gap={6} mt={14} mb={4} c="var(--mantine-color-brand-7)">
          <Text fz="md" fw={600} inherit>
            Open tracker
          </Text>
          <IconArrowRight size={16} aria-hidden />
        </Group>
      ) : loading ? (
        <Skeleton h={34} w={56} mt={10} mb={4} />
      ) : (
        <Text fz={32} fw={650} lh={1.15} mt={8} className="tabular" style={{ letterSpacing: '-0.015em' }}>
          {c.value ?? 0}
        </Text>
      )}
      <Text fz="xs" c="dimmed" mt={4} className="tabular" lineClamp={2} mih="2.8em">
        {c.caption}
      </Text>
    </UnstyledButton>
  );
}

/**
 * The dashboard's twelve tiles, in the order Makin's clients know them: dossiers, queries and compliance, Free Sale
 * Certificates, registration certificates, approved artworks and 3D images. Each opens the list it counts.
 */
export function DashboardCounts() {
  const summary = useClientSummary();
  const s = summary.data;
  const fscTab = s && !s.fsc.nutra && s.fsc.herbal ? 'fsc_herbal' : 'fsc_neuta';
  const tiles: Tile[] = [
    {
      label: 'Total Submitted Dossiers',
      value: s?.submittedDossiers,
      caption: 'Filed for your company',
      to: '/requests?tab=filed',
      icon: IconFileCheck,
    },
    {
      label: 'Dossier Under Preparation',
      value: s?.requisitions.underProcess,
      caption: 'Received, in process or under RA review',
      to: '/requests?view=open',
      icon: IconClockHour4,
    },
    {
      label: 'Total Received Queries',
      value: s?.queries.total,
      caption: 'Raised on your dossiers',
      to: '/requests?tab=queries',
      icon: IconMessage2,
    },
    {
      label: 'Submitted Compliance',
      value: s?.queries.answered,
      caption: 'Queries Makin answered',
      to: '/requests?tab=queries&status=answered',
      icon: IconMessageCheck,
    },
    {
      label: 'Pending Queries',
      value: s?.queries.open,
      caption: 'Waiting for Makin’s compliance',
      to: '/requests?tab=queries&status=open',
      icon: IconMessageExclamation,
    },
    {
      label: 'Total Available FSC',
      value: s?.fsc.available,
      caption: 'Free Sale Certificates',
      to: `/requests?tab=certificates&certs=${fscTab}`,
      icon: IconCertificate,
    },
    {
      label: 'FSC Tracker',
      tracker: true,
      caption: s ? `FSC Nutra ${s.fsc.nutra} · FSC Herbal ${s.fsc.herbal}` : ' ',
      to: `/requests?tab=certificates&certs=${fscTab}`,
      icon: IconListCheck,
    },
    {
      label: 'Total RC',
      value: s?.certificates.onFile,
      caption: 'Registration certificates on file',
      to: '/requests?tab=filed',
      icon: IconFileCertificate,
    },
    {
      label: 'Awaited RC',
      value: s?.certificates.pending,
      caption: s?.certificates.pending ? 'Upload them from Filed dossiers' : 'Every dossier has one',
      to: '/requests?tab=filed&rc=missing',
      icon: IconFileAlert,
    },
    {
      label: 'RC Tracker',
      tracker: true,
      caption: s?.certificates.renewalDue ? (
        <Text span inherit c="var(--mantine-color-yellow-light-color)" fw={600}>
          {s.certificates.renewalDue} due for renewal
        </Text>
      ) : (
        'Validity of each certificate'
      ),
      to: s?.certificates.renewalDue ? '/requests?tab=filed&rc=renewal' : '/requests?tab=filed',
      icon: IconCalendarStats,
    },
    {
      label: 'PDF of Approved Artworks',
      value: s?.media.artworks,
      caption: 'Final approved artworks and labels',
      to: '/requests?tab=artworks',
      icon: IconFileTypePdf,
    },
    {
      label: '3D Images of Commercial Products',
      value: s?.media.images,
      caption: 'Products in their commercial packaging',
      to: '/requests?tab=artworks',
      icon: IconCube,
    },
  ];
  if (summary.isError) return null;
  return (
    <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="md" verticalSpacing="md" component="nav" aria-label="Your numbers">
      {tiles.map((c) => (
        <DashboardTile key={c.label} c={c} loading={summary.isLoading} />
      ))}
    </SimpleGrid>
  );
}
